/* global fbq */

import React, { useState, useEffect } from 'react';
import LPTooltip from '../../components/LPTooltip';
import PremiumSelect from '../../components/PremiumSelect';
import { faUserGroup, faUserClock, faWifi, faUser, faLocationCrosshairs, faChevronDown, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Snackbar, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

import { Helmet } from 'react-helmet-async';
import OG from '../../assets/og/net.png';

const RANKS = [
  'Iron IV','Iron III','Iron II','Iron I',
  'Bronze IV','Bronze III','Bronze II','Bronze I',
  'Silver IV','Silver III','Silver II','Silver I',
  'Gold IV','Gold III','Gold II','Gold I',
  'Platinum IV','Platinum III','Platinum II','Platinum I',
  'Emerald IV','Emerald III','Emerald II','Emerald I',
  'Diamond IV','Diamond III','Diamond II'
];

function romanToIntItoIV(s) {
  switch (s) {
    case 'I': return 1;
    case 'II': return 2;
    case 'III': return 3;
    case 'IV': return 4;
    default: throw new Error(`Unsupported numeral: ${s}`);
  }
}

const roles = [
  { key: 'Top', label: 'Top', icon: require('../../assets/Positions/Top.png') },
  { key: 'Jungle', label: 'Jungle', icon: require('../../assets/Positions/Jungle.png') },
  { key: 'Mid', label: 'Mid', icon: require('../../assets/Positions/Mid.png') },
  { key: 'ADC', label: 'ADC', icon: require('../../assets/Positions/ADC.png') },
  { key: 'Support', label: 'Support', icon: require('../../assets/Positions/Support.png') },
];

const rawChamps = require('../../constants/champions.json');
const champions = Array.isArray(rawChamps) ? rawChamps : Object.values(rawChamps);

export default function NetBoosting({ updatePrice, order_details, checkoutInit, loading }) {
  const { getAccessToken } = useAuth();
  const currency = localStorage.getItem('currency') || 'USD';

  const [currentRank, setCurrentRank] = useState(RANKS[8]);
  const [lpGain, setLpGain] = useState('23-29');
  const [numNetWins, setNumNetWins] = useState('5');
  const [queueType, setQueueType] = useState('Solo/Duo');
  const [region, setRegion] = useState('NA');
  const [addons, setAddons] = useState([]);
  const [searchChamp, setSearchChamp] = useState('');
  const [selectedChamps, setSelectedChamps] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [addonsOpen, setAddonsOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open:false, severity:'success', message:'' });
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setInitialLoad(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('net-boosting-data');
    if (stored) {
      const parsed = JSON.parse(stored);
      setCurrentRank(parsed.currentRank || RANKS[8]);
      setLpGain(parsed.lpGain || '23-29');
      setNumNetWins(parsed.numNetWins || '5');
      setQueueType(parsed.queueType || 'Solo/Duo');
      setRegion(parsed.region || 'NA');
      setAddons(parsed.addons || []);
      setSelectedChamps(parsed.selectedChamps || []);
      setSelectedRoles(parsed.selectedRoles || []);
      calculatePrice();
      order_details([
        ['Boost Type', 'Net Wins'],
        ['Current Rank', parsed.currentRank || RANKS[8]],
        ['Net Wins', parsed.numNetWins || '5'],
        ['Queue & Region', `${parsed.queueType || 'Solo/Duo'} (${parsed.region || 'NA'})`],
      ]);
    } else {
      calculatePrice();
      order_details([
        ['Boost Type', 'Net Wins'],
        ['Current Rank', RANKS[8]],
        ['Net Wins', '5'],
        ['Queue & Region', `Solo/Duo (NA)`],
      ]);
    }
  }, []);
  
  const pushToDataLayer = (payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
  };

  useEffect(() => {
    if (initialLoad) return;
    updateOrderDataCookie();
    calculatePrice();
  }, [currentRank, lpGain, numNetWins, addons]);

  useEffect(() => {
    if (initialLoad) return;
    updateOrderDataCookie();
  }, [queueType, region, selectedChamps, selectedRoles]);

  const updateOrderDataCookie = () => {
    order_details([
        ['Boost Type', 'Net Wins'],
        ['Current Rank', currentRank],
        ['Net Wins', numNetWins],
        ['Queue & Region', `${queueType} (${region})`]
    ]);
    localStorage.setItem('net-boosting-data', JSON.stringify({ 
        currentRank, 
        lpGain, 
        numNetWins, 
        queueType, 
        region, 
        addons, 
        selectedChamps, 
        selectedRoles 
    }));
  };

  const calculatePrice = () => {
    loading(true);
    if(initialLoad) {
        const stored = localStorage.getItem('net-boosting-data');
        if (stored) {
            const parsed = JSON.parse(stored);
            axios.post(process.env.REACT_APP_API_URL + '/price-calculation-net-wins-boost', {
                rank: parsed.currentRank.split(' ')[0],
                division: romanToIntItoIV(parsed.currentRank.split(' ')[1]),
                numNetWins: parseInt(parsed.numNetWins, 10),
                LPGains: parsed.lpGain.split('-')[0] === '30+' ? 30 : parseInt(parsed.lpGain.split('-')[0]),
                addons: parsed.addons,
                currency
            }).then(resp => {
                loading(false);
                updatePrice(resp.data.totalPrice);
            }).catch(err => {
                loading(false);
                updatePrice(0);
                setSnackbar({ open:true, severity:'error', message:'Failed to calculate price. Please try again.' });
                console.error(err);
            });
            return;
        }
    }
    const rank = currentRank.split(' ')[0];
    const division = romanToIntItoIV(currentRank.split(' ')[1]);
    const lpGainParsed = lpGain.split('-')[0] === '30+' ? 30 : parseInt(lpGain.split('-')[0]);
    const wins = parseInt(numNetWins, 10);
    axios.post(process.env.REACT_APP_API_URL + '/price-calculation-net-wins-boost', {
      rank,
      division,
      numNetWins: wins,
      LPGains: lpGainParsed,
      addons,
      currency
    }).then(resp => {
      loading(false);
      updatePrice(resp.data.totalPrice);
    }).catch(err => {
      loading(false);
      updatePrice(0);
      setSnackbar({ open:true, severity:'error', message:'Failed to calculate price. Please try again.' });
      console.error(err);
    });
  };

  const handleClose = () => setSnackbar(s => ({ ...s, open:false }));

  const toggleAddon = label => {
    setAddons(prev => {
      let next = prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label];
      if (label === 'Duo') next = next.filter(l => l !== 'Solo Only');
      if (label === 'Solo Only') next = next.filter(l => l !== 'Duo');
      return next;
    });
  };

  const toggleRole = key => setSelectedRoles(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  const toggleChamp = name => setSelectedChamps(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);

  useEffect(() => {
    if (initialLoad) return;
    if (!addons.includes('Specific Roles')) setSelectedRoles([]);
    if (!addons.includes('Specific Champions')) setSelectedChamps([]);
  }, [addons]);

  const filtered = champions.filter(c => c.toLowerCase().includes(searchChamp.toLowerCase()));

  useEffect(() => {
    if(checkoutInit) {
      let mergedAddons = [...addons, ...checkoutInit.addons];
      if (typeof fbq !== 'undefined') {
        fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: ['net-wins'],
          content_name: 'Net Win Boosting',
          currency: currency,
          value: checkoutInit.price,
          num_items: 1,
          contents: [
            {
              id: 'net-wins',
              quantity: 1,
              item_price: checkoutInit.price
            }
          ]
        });
      }
      pushToDataLayer({
        event: 'begin_checkout',
        ecommerce: {
          value:    checkoutInit.price,
          currency: currency
        }
      })
      axios.post(process.env.REACT_APP_API_URL + `/user/stripe-checkout`, {
        order_type: 'boost', 
        boost_type: 'net_wins', 
        server: region, 
        coupon: checkoutInit.coupon, 
        net_wins: {
          initial_rank: currentRank.split(' ')[0],
          initial_division: romanToIntItoIV(currentRank.split(' ')[1]),
          current_LP_gains: lpGain.split('-')[0] === '30+' ? 30 : parseInt(lpGain.split('-')[0]),
          num_net_wins: parseInt(numNetWins, 10),
          queue_type: queueType,
          addons: mergedAddons,
          roles: selectedRoles,
          champions: selectedChamps,
        },
        currency: currency
      }, { headers: { Authorization: getAccessToken() }
      }).then((response) => {
          window.location.href = response.data.url;
      }).catch((error) => {
          console.log(error);
      });
    }
  }, [checkoutInit]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>LoL Net Win Boosting – Guaranteed Ranked Wins | EloFactory.gg</title>
        <meta name="description" content="Ensure net positive wins with our LoL net wins boost. Guaranteed extra wins for a higher MMR and rank. Safe &amp; quick service – climb the ladder now!" />
        <link rel="canonical" href="https://elofactory.gg/get-boosting/net-wins" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="LoL Net Wins Boost – Climb with Guaranteed Wins" />
        <meta property="og:description" content="Stuck with a 50% win rate? Our net win boosting service guarantees additional ranked wins to improve your MMR. Get the wins you need to rank up!" />
        <meta property="og:url" content="https://elofactory.gg/get-boosting/net-wins" />
        <meta property="og:image" content={OG} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="EloFactory.gg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LoL Net Win Boost – Raise Your Win Rate, Raise Your Rank" />
        <meta name="twitter:description" content="Convert losses into wins! Our net wins boost gives you an edge by adding guaranteed wins to your ranked record. Safe, anonymous boosting to lift your rank." />
        <meta name="twitter:image" content={OG} />
        <meta name="twitter:site" content="@elofactorygg" />
        <meta name="twitter:creator" content="@elofactorygg" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hrefLang="en-US" href="https://elofactory.gg/get-boosting/net-wins" />
        <script type="application/ld+json">{`
        {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "LoL Net Wins Boosting",
        "description": "Ranked net win boosting service that guarantees a number of extra wins in League of Legends ranked games to improve MMR.",
        "provider": {
        "@type": "Organization",
        "name": "EloFactory",
        "url": "https://elofactory.gg"
        },
        "areaServed": "North America",
        "serviceType": "Net Win Ranked Boost",
        "url": "https://elofactory.gg/get-boosting/net-wins"
        }
        `}</script>
      </Helmet>

        {/* Current Rank & LP Gains */}
        <div className="rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] p-6 text-white flex flex-col md:flex-row gap-8">
            <div className="flex-1">
                <h2 className="mb-4 text-lg font-semibold text-[#00baff]">Current Rank</h2>
                <div className="flex items-start gap-4 items-center">
                    <img
                      id="currentEmblem"
                      src={require(`../../assets/Emblems/${currentRank.split(' ')[0]}_Emblem.png`)}
                      alt={`${currentRank} Rank Icon`}
                      title={`League of Legends ${currentRank} rank`}
                      className="h-28 w-28 object-contain"
                      width={112}
                      height={112}
                    />
                    <div className="flex w-full flex-col gap-3">
                        <PremiumSelect
                            label="Rank & Division"
                            value={currentRank}
                            onChange={setCurrentRank}
                            options={RANKS}
                        />
                        <PremiumSelect
                            label="Current LP Gains"
                            value={lpGain}
                            onChange={setLpGain}
                            options={['10-22', '23-29', '30+']}
                            labelTooltipText="Estimate your average LP gain per win."
                        />
                        <PremiumSelect
                            label="Number of Net Wins"
                            value={numNetWins}
                            onChange={setNumNetWins}
                            options={['1', '2', '3', '4', '5']}
                            labelTooltipText="Select the number of net wins you want to achieve."
                        />
                    </div>
                </div>
            </div>
        </div>
        {/* Queue & Region */}
        <div className="rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] p-6 text-white">
            <h2 className="mb-4 text-lg font-semibold text-[#00baff]">Queue & Region</h2>
            <div className="flex flex-wrap gap-4">
                <div className="min-w-[200px] flex-1">
                    <PremiumSelect
                    label="Queue Type"
                    value={queueType}
                    onChange={setQueueType}
                    options={['Solo/Duo', 'Flex']}
                    />
                </div>
                <div className="min-w-[200px] flex-1">
                    <PremiumSelect
                    label="Account Region"
                    value={region}
                    onChange={setRegion}
                    options={['NA', 'EUW', 'EUNE', 'LAN']}
                    />
                </div>
            </div>
        </div>

        {/* Add-ons */}
              <div className={`
                rounded-lg bg-[var(--bg-surface)] border 
                border-[var(--border)] p-6 text-white flex 
                flex-col md:flex-row md:cursor-auto ${addonsOpen ? 'cursor-auto' : 'cursor-pointer'}`}
                onClick={() => {
                  //check if it is mobile
                  if (window.innerWidth <= 768 && !addonsOpen) {
                    setAddonsOpen(true)
                    return
                  }
                }}
              >
                {/* ─── Left: tight add-ons list ─── */}
                {/* Header + toggle: only on mobile */}
                <div className={`flex items-center justify-between md:hidden cursor-pointer md:cursor-auto`}
                onClick={() => {
                  if (window.innerWidth <= 768 && addonsOpen) {
                    setAddonsOpen(!addonsOpen)
                    return
                  }
                }}>
                  <h2 className="text-lg font-semibold text-[#00BAFF]">Advanced Settings</h2>
                  <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`
                        text-white
                        transform transition-transform duration-300 ease-in-out
                        ${addonsOpen ? 'rotate-180' : 'rotate-0'}
                      `}
                      width={16}
                      height={16}
                    />        
                  </div>
                <div
                className={`
                  overflow-hidden transition-[max-height] duration-300 ease-in-out
                  ${addonsOpen ? 'max-h-[2000px]' : 'max-h-0'}
                  md:max-h-none md:block space-y-4
                `}
              >
                <h2 className="text-lg font-semibold text-[#00baff] hidden md:block">Advanced Settings</h2>
            {[
                ['Duo', '+60%', 'Play alongside the booster in Duo Queue.', faUserGroup],
                ['Priority', '+15%', 'Your order will be prioritized before others.', faUserClock],
                ['Solo Only', '+25%', 'The booster will not duo queue with anyone.', faUser],
                ['Streaming', '+15%', 'Watch your boost live via private stream.', faWifi],
            ].map(([label, extra, desc, icon]) => (
                <div key={label} className="flex items-center justify-between">
                <div className="flex items-center mr-4">
                    <FontAwesomeIcon icon={icon} className="w-4 h-4 mr-2 text-[#00baff]" />
                    <span className="text-sm text-white mr-2">{label}</span>
                    <span className="text-sm text-[#00baff] mr-2">{extra}</span>
                    <LPTooltip tooltipText={desc} />
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch ml-2"
                    checked={addons.includes(label)}
                    onChange={() => toggleAddon(label)}
                />
                </div>
            ))}
    
            <div className="space-y-4">
                {/* Header with toggle */}
                <div className="flex items-center justify-between" key="Specific Roles">
                <div className="flex items-center">
                    <FontAwesomeIcon
                    icon={faLocationCrosshairs}
                    className="w-4 h-4 mr-2 text-[#00baff]"
                    />
                    <span className="text-sm text-white mr-2">Specific Roles</span>
                    <span className="text-sm text-[#00baff] mr-2">+15%</span>
                    <LPTooltip tooltipText="Choose a preferred role (e.g. Mid, ADC)." />
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch"
                    checked={addons.includes("Specific Roles")}
                    onChange={() => toggleAddon("Specific Roles")}
                />
                </div>
    
                {/* Role buttons */}
                <div className={`
                flex flex-wrap gap-2 justify-between
                ${addons.includes("Specific Roles") ? "flex" : "hidden"} md:flex
                `}>
                {roles.map(({ key, icon }) => (
                    <button
                    key={key}
                    onClick={() => toggleRole(key)}
                    disabled={!addons.includes("Specific Roles")}
                    aria-pressed={selectedRoles.includes(key)}
                    className={
                        `
                        group relative flex items-center justify-center
                        rounded-md
                        transition-transform transition-shadow transition-opacity duration-200 ease-out
                        motion-reduce:transition-none
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${
                        selectedRoles.includes(key)
                            ? "shadow-[0_0_6px_rgba(0,186,255,0.6)]  bg-white/10"
                            : "shadow-sm bg-transparent"
                        }
                        hover:shadow-[0_0_4px_rgba(0,186,255,0.4)] hover:bg-white/5
                        `
                    }
                    >
                    <img
                        src={icon}
                        alt={`${key} Role Icon`}
                        title={`League of Legends ${key} role`}
                        className="w-8 h-8"
                        width={32}
                        height={32}
                    />
                    </button>
                ))}
                </div>
            </div>
            
            </div>
    
            {/* ─── Divider ─── */}
            <div className="mx-6 border-l border-[var(--border)] hidden md:block" />
    
            {/* ─── Right: full-width panels ─── */}
            <div className={`flex-1 space-y-4 pl-0 md:pl-2
              overflow-hidden transition-all duration-300 ease-in-out
              ${addonsOpen ? 'max-h-[2000px] mt-4' : 'max-h-0 mt-0 space-y-0'}
              md:max-h-none md:block`}
            >
            {/* Specific Champions panel */}
            <div className="space-y-4">
                {/* header + toggle */}
                <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faUsers} className="w-4 h-4 mr-2 text-[#00baff]" />
                    <span className="text-sm text-white mr-2">Specific Champions</span>
                    <span className="text-sm text-[#00baff] mr-2">+20%</span>
                    <LPTooltip tooltipText="Choose specific champions to play." />
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch"
                    checked={addons.includes("Specific Champions")}
                    onChange={() => toggleAddon("Specific Champions")}
                />
                </div>
    
                {/* ─── Mobile-hide / desktop-show wrapper ─── */}
            <div className={`${addons.includes("Specific Champions") ? 'block' : 'hidden'} md:block space-y-2`}>
                
                {/* ─── Search box (unstyled, just your original class) ─── */}
                <input
                type="text"
                placeholder="Search champions…"
                value={searchChamp}
                onChange={e => setSearchChamp(e.target.value)}
                disabled={!addons.includes("Specific Champions")}
                className="search-input w-full rounded-md bg-[var(--bg-surface)] border border-[var(--border)] px-3 py-2 text-sm text-white placeholder-[var(--secondary-text)] focus:outline-none focus:ring-1 focus:ring-[var(--focus-ring)]"
                />
    
                {/* ─── Champions container: grid when enabled, .is-disabled when off ─── */}
                <div
                className={`
                    champions-container
                    scrollbar-custom
                    h-44
                    ${addons.includes("Specific Champions")
                    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 overflow-y-auto rounded-md bg-[var(--bg-surface)] border border-[var(--border)]'
                    : 'is-disabled'}
                `}
                >
                {filtered.map(champ => (
                    <label key={champ} className="champion-item flex items-center p-1 rounded">
                    <input
                        type="checkbox"
                        checked={selectedChamps.includes(champ)}
                        onChange={() => {
                          //champion selection maximum of 10
                          if (selectedChamps.length >= 10 && !selectedChamps.includes(champ)) {
                            setSnackbar({ open: true, severity: 'warning', message: 'You can only select up to 10 champions.' });
                            return;
                          }
                          toggleChamp(champ)
                        }}
                        disabled={!addons.includes("Specific Champions")}
                    />
                    <span className="champion-name">{champ}</span>
                    </label>
                ))}
                </div>
            </div>
    
            </div>
            </div>
        </div>

        <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
        >
            <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width:'100%' }}>
                {snackbar.message}
            </Alert>
        </Snackbar>
    </>)
}
